import axios from 'axios';
import { toast } from './toast';
import { isBudgetConflict, withBudgetConfirm } from './utils/aiCost.js';
import { explainParams, isInjectionConflict, withInjectionConfirm } from './utils/analysisSignals.js';

const api = axios.create({
    baseURL: '/api',
    withCredentials: true,
});

// Global response interceptor — shows a toast for every failed request
// unless the caller sets config._silent = true.
// A 401 on any route other than /auth/login, /auth/me, and /auth/change-password
// triggers a redirect to /login.
api.interceptors.response.use(
    response => response,
    error => {
        const url = error.config?.url ?? '';
        const status = error?.response?.status;

        // /auth/change-password 401s mean "wrong current password", not an expired session.
        if (status === 401 && !url.includes('/auth/login') && !url.includes('/auth/me') && !url.includes('/auth/change-password')) {
            window.dispatchEvent(new CustomEvent('auth:require-login'));
            return Promise.reject(error);
        }

        // A soft-budget 409 on a budget-aware call, and the prompt-injection 409 on an
        // injection-aware Explain, become confirmations, not toasts.
        const budgetQuestion = error.config?._budgetAware && isBudgetConflict(error);
        const injectionQuestion = error.config?._injectionAware && isInjectionConflict(error);
        if (!error.config?._silent && !budgetQuestion && !injectionQuestion) {
            const message =
                error?.response?.data?.error ||
                error?.message ||
                'An unexpected error occurred';
            toast.error(message);
        }
        return Promise.reject(error);
    }
);

// In-flight dedup: if a second caller invokes this while the first request is
// still pending, both share the same promise. The cache clears on settle, so
// subsequent calls (after a mutation) always fetch fresh.
let _folderTreeInflight = null;
export const getFolderTree = () => {
    if (_folderTreeInflight) return _folderTreeInflight;
    _folderTreeInflight = api.get('/folders/tree')
        .then(res => res.data)
        .finally(() => { _folderTreeInflight = null; });
    return _folderTreeInflight;
};
// Pass { _silent: true } to suppress the global error toast (e.g. background sidebar sync).
export const getFolder = (id, options = {}) => api.get(`/folders/${id}`, options).then(res => res.data);
export const createFolder = (name, parentId) => api.post('/folders', { name, parent_id: parentId }).then(res => res.data);
export const deleteFolder = (id) => api.delete(`/folders/${id}`);
export const deleteFolders = (ids) => api.post('/folders/bulk-delete', { ids });
export const updateFolder = (id, name) => api.patch(`/folders/${id}`, { name }).then(res => res.data);
export const moveFolder = (id, parentId) => api.patch(`/folders/${id}/parent`, { parent_id: parentId });
export const bulkMoveFolders = (ids, parentId) => api.post('/folders/bulk-move', { ids, parent_id: parentId });

export const getCategories = (page = 1, pageSize = 10, search = '') => {
    const offset = (page - 1) * pageSize;
    const q = search ? `&q=${encodeURIComponent(search)}` : '';
    return api.get(`/categories?limit=${pageSize}&offset=${offset}${q}`).then(res => res.data);
};
// Every category, unpaginated — the store skips LIMIT/OFFSET when limit <= 0.
// Pickers and filters need the whole list; paginating them would silently hide
// categories past the first page. Read it through CategoriesContext rather than
// calling this directly, so one copy is shared across the app.
export const getAllCategories = () => api.get('/categories?limit=0').then(res => res.data);
export const createCategory = (name, description) => api.post('/categories', { name, description }).then(res => res.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const deleteCategories = (ids) => api.post('/categories/bulk-delete', { ids });

// opts.view: 'list' uses the slimmer server response (omits Steps/CustomValues,
// returns steps_count). Defaults to full-detail response for backwards compatibility.
export const getTests = (folderIds = [], categoryId, opts = {}) => {
    let params = new URLSearchParams();
    if (folderIds && folderIds.length > 0) {
        params.append('folder_ids', folderIds.join(','));
    }
    if (categoryId) {
        params.append('category_id', categoryId);
    }
    if (opts.view) {
        params.append('view', opts.view);
    }
    return api.get('/tests', { params }).then(res => res.data);
};

export const getTest = (id, config) => api.get(`/tests/${id}`, config).then(res => res.data);

export const createTest = (name, folderId, description) => api.post('/tests', { name, folder_id: folderId, description }).then(res => res.data);
export const updateTest = (test) => api.put(`/tests/${test.id}`, test).then(res => res.data);
export const moveTest = (testId, folderId) => api.put(`/tests/${testId}`, { folder_id: folderId }).then(res => res.data);
export const deleteTest = (id) => api.delete(`/tests/${id}`);
export const deleteTests = (ids) => api.post('/tests/bulk-delete', { ids });
export const exportTests = (ids, fields) =>
    api.post('/tests/export', { ids, fields }, { responseType: 'blob' }).then(res => {
        const disposition = res.headers['content-disposition'] || '';
        const match = disposition.match(/filename="?([^"]+)"?/);
        const filename = match ? match[1] : `ttgo-export-${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.json`;
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    });
export const assignCategory = (testId, categoryId) => api.post(`/tests/${testId}/categories`, { category_id: categoryId });

export const getTestRuns = (categoryIds, status, sortBy, order, page = 1, pageSize = 20, folderID = null, dateFilters = {}) => {
    let params = new URLSearchParams();
    if (Array.isArray(categoryIds) && categoryIds.length > 0) params.append('category_ids', categoryIds.join(','));
    else if (typeof categoryIds === 'string' && categoryIds) params.append('category_id', categoryIds);
    if (status) params.append('status', status);
    if (sortBy) params.append('sort_by', sortBy);
    if (order) params.append('order', order);
    if (folderID !== null && folderID !== undefined) params.append('run_folder_id', folderID);
    if (dateFilters.createdFrom) params.append('created_from', dateFilters.createdFrom);
    if (dateFilters.createdTo) params.append('created_to', dateFilters.createdTo);
    if (dateFilters.updatedFrom) params.append('updated_from', dateFilters.updatedFrom);
    if (dateFilters.updatedTo) params.append('updated_to', dateFilters.updatedTo);
    if (dateFilters.assigneeId) params.append('assignee_id', dateFilters.assigneeId);

    const offset = (page - 1) * pageSize;
    params.append('limit', pageSize);
    params.append('offset', offset);

    return api.get('/runs', { params }).then(res => res.data);
};
export const getTestRun = (id) => api.get(`/runs/${id}`).then(res => res.data);
export const createTestRun = (categoryId, name, runFolderId = null, testCaseIds = null) =>
    api.post('/runs', {
        category_id: categoryId || null,
        name,
        run_folder_id: runFolderId,
        ...(Array.isArray(testCaseIds) && testCaseIds.length > 0 ? { test_case_ids: testCaseIds } : {}),
    }).then(res => res.data);
export const updateTestRun = (id, name, categoryId, status) => api.put(`/runs/${id}`, { name, ...(categoryId ? { category_id: categoryId } : {}), ...(status ? { status } : {}) }).then(res => res.data);
export const completeTestRun = (id) => api.post(`/runs/${id}/complete`).then(res => res.data);
export const reopenTestRun = (id) => api.post(`/runs/${id}/reopen`).then(res => res.data);
export const getAssignableUsers = () => api.get('/users/assignable').then(res => res.data.users || []);
export const assignRun = (runId, assigneeId) =>
    api.put(`/runs/${runId}/assignee`, { assignee_id: assigneeId || null }).then(res => res.data);
export const deleteTestRun = (id) => api.delete(`/runs/${id}`);
export const deleteTestRuns = (ids) => api.post('/runs/bulk-delete', { ids });

export const addRunResultsBulk = (runId, testCaseIds) =>
    api.post(`/runs/${runId}/results/bulk`, { test_case_ids: testCaseIds }).then(res => res.data);
export const deleteRunResult = (runId, resultId) => api.delete(`/runs/${runId}/results/${resultId}`);
export const updateRunResult = (runId, resultId, data) => {
    const payload = typeof data === 'string' ? { status: data } : data;
    return api.put(`/runs/${runId}/results/${resultId}`, payload);
};
export const retryRunResult = (runId, resultId) =>
    api.post(`/runs/${runId}/results/${resultId}/retry`).then(res => res.data);
// Omitting `status` is meaningful, not just tidy: a body carrying result_ids +
// defect_type and NO status puts the endpoint in triage mode, where the defect type is
// applied only to rows whose stored status is a failure and no status is ever written.
// Sending status: "" or null instead would be the same request, but the conditional
// spread keeps the wire shape explicit and mirrors defect_type.
export const bulkUpdateRunResults = (runId, resultIds, status, defectType) =>
    api.post(`/runs/${runId}/results/bulk-update`, {
        result_ids: resultIds,
        ...(status ? { status } : {}),
        ...(defectType ? { defect_type: defectType } : {}),
    }).then(res => res.data);
export const uploadScreenshots = (runId, resultId, files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('screenshots', f));
    return api.post(`/runs/${runId}/results/${resultId}/screenshots`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
    }).then(res => res.data);
};

// ── Comments ──
export const listRunComments = (runId) =>
    api.get(`/runs/${runId}/comments`).then(res => res.data);
export const addRunComment = (runId, content) =>
    api.post(`/runs/${runId}/comments`, { content }).then(res => res.data);
export const listResultComments = (runId, resultId) =>
    api.get(`/runs/${runId}/results/${resultId}/comments`).then(res => res.data);
export const addResultComment = (runId, resultId, content) =>
    api.post(`/runs/${runId}/results/${resultId}/comments`, { content }).then(res => res.data);
export const updateComment = (commentId, content) =>
    api.put(`/comments/${commentId}`, { content }).then(res => res.data);
export const deleteComment = (commentId) =>
    api.delete(`/comments/${commentId}`);

export const getCustomFields = () => api.get('/custom-fields').then(res => res.data);
export const createCustomField = (name, type, options, isMandatory) => api.post('/custom-fields', { name, type, options: Array.isArray(options) && options.length ? options : null, is_mandatory: isMandatory }).then(res => res.data);
export const deleteCustomField = (id) => api.delete(`/custom-fields/${id}`);


// API Tokens
export const listTokens = () => api.get('/tokens').then(res => res.data);
export const createToken = (description, scope) => api.post('/tokens', { description, scope }).then(res => res.data);
export const deleteToken = (id) => api.delete(`/tokens/${id}`);

// Webhooks
export const listWebhooks = () => api.get('/webhooks').then(res => res.data);
export const createWebhook = (url, description, eventType = 'run.completed') =>
    api.post('/webhooks', { url, description, event_type: eventType }).then(res => res.data);
export const deleteWebhook = (id) => api.delete(`/webhooks/${id}`);
export const rotateWebhookSecret = (id) => api.post(`/webhooks/${id}/rotate-secret`).then(res => res.data);

// Analytics (012-analytics-refactor: enhanced with global filter support)
export const getAnalyticsSummary = (filters = {}, signal) =>
    api.get('/analytics/summary', { params: filters, signal }).then(res => res.data);
export const getAnalyticsTrend = (filters = {}, signal) =>
    api.get('/analytics/trend', { params: filters, signal }).then(res => res.data);
export const getAnalyticsFlaky = (filters = {}, signal) =>
    api.get('/analytics/flaky', { params: filters, signal }).then(res => res.data);
export const getAnalyticsMostFailed = (filters = {}, signal) =>
    api.get('/analytics/most-failed', { params: filters, signal }).then(res => res.data);
export const getAnalyticsDuration = (filters = {}, signal) =>
    api.get('/analytics/duration', { params: filters, signal }).then(res => res.data);
export const getAnalyticsDurationTop = (filters = {}, signal) =>
    api.get('/analytics/duration/top', { params: filters, signal }).then(res => res.data);
export const getAnalyticsComponentHealth = (filters = {}, signal) =>
    api.get('/analytics/component-health', { params: filters, signal }).then(res => res.data);
export const getAnalyticsGrowth = (filters = {}, signal) =>
    api.get('/analytics/growth', { params: filters, signal }).then(res => res.data);
export const getAnalyticsPassingRate = (filters = {}, signal) =>
    api.get('/analytics/passing-rate', { params: filters, signal }).then(res => res.data);
export const getAnalyticsUniqueBugs = (filters = {}, signal) =>
    api.get('/analytics/unique-bugs', { params: filters, signal }).then(res => res.data);
export const getAnalyticsActivity = (filters = {}, signal) =>
    api.get('/analytics/activity', { params: filters, signal }).then(res => res.data);
export const getAnalyticsCompareRuns = (run1, run2, signal) =>
    api.get('/analytics/compare-runs', { params: { run1, run2 }, signal }).then(res => res.data);
export const getAIGenerationReport = (params, signal) =>
    api.get('/ai-generations/reports/summary', { params, signal }).then(res => res.data);

// Run Folders
export const getRunFolders = () => api.get('/run-folders').then(res => res.data);
export const getRunFolderTree = () => api.get('/run-folders?view=tree').then(res => res.data);
export const createRunFolder = (name, parentId = null) => api.post('/run-folders', { name, parent_id: parentId }).then(res => res.data);
export const updateRunFolder = (id, name) => api.patch(`/run-folders/${id}`, { name }).then(res => res.data);
export const reorderRunFolder = (id, displayOrder) => api.patch(`/run-folders/${id}/order`, { display_order: displayOrder }).then(res => res.data);
export const moveRunFolder = (id, parentId) => api.patch(`/run-folders/${id}/parent`, { parent_id: parentId }).then(res => res.data);
export const deleteRunFolder = (id) => api.delete(`/run-folders/${id}`);
export const assignRunToFolder = (runId, folderId) => api.patch(`/runs/${runId}/folder`, { run_folder_id: folderId }).then(res => res.data);
export const copyTestRun = (runId, name = '', runFolderId = null) => api.post(`/runs/${runId}/copy`, { name, run_folder_id: runFolderId }).then(res => res.data);
export const copyRunFolder = (folderId, name = '', parentId = null) => api.post(`/run-folders/${folderId}/copy`, { name, parent_id: parentId }).then(res => res.data);

// ── Authentication ──
// Demo-stand info (public, no auth). _silent — a non-demo instance must not toast.
export const getDemoInfo = () => api.get('/demo', { _silent: true }).then(res => res.data);

export const auth = {
    login: (email, password) =>
        api.post('/auth/login', { email, password }).then(res => res.data),
    logout: () =>
        api.post('/auth/logout').then(res => res.data),
    me: () =>
        api.get('/auth/me', { _silent: true }).then(res => res.data),
    changePassword: (currentPw, newPw) =>
        api.post('/auth/change-password', {
            current_password: currentPw,
            new_password: newPw,
        }, { _silent: true }).then(res => res.data),
    needsSetup: () =>
        api.get('/auth/needs-setup', { _silent: true }).then(res => res.data),
    setup: (email, password) =>
        api.post('/auth/setup', { email, password }, { _silent: true }).then(res => res.data),
};

// ── Demo data seeding (admin only) ──
export const seed = {
    status: () => api.get('/seed').then(res => res.data),
    load: () => api.post('/seed').then(res => res.data),
    loadAI: () => api.post('/seed/ai').then(res => res.data),
    remove: () => api.delete('/seed').then(res => res.data),
    // The backend requires this exact confirmation string in the body (a
    // server-side guard, separate from the UI's "type ERASE" gate); without it
    // the reset is refused with 400 and nothing is erased. Axios sends a DELETE
    // body via the `data` config key.
    resetAll: () => api.delete('/admin/reset', { data: { confirm: 'CONFIRM RESET' } }).then(res => res.data),
};

// ── User management (admin only) ──
export const users = {
    list: (includeDeleted = false) =>
        api.get('/users', { params: includeDeleted ? { include_deleted: 'true' } : {} }).then(res => res.data),
    create: (data) =>
        api.post('/users', data).then(res => res.data),
    update: (id, data) =>
        api.patch(`/users/${id}`, data).then(res => res.data),
    delete: (id) =>
        api.delete(`/users/${id}`).then(res => res.data),
    restore: (id) =>
        api.post(`/users/${id}/restore`).then(res => res.data),
};

// ── Version history (006-test-case-versioning) ──
export const versions = {
    list: (testCaseId) =>
        api.get(`/tests/${testCaseId}/versions`).then(res => res.data),
    get: (testCaseId, versionId) =>
        api.get(`/tests/${testCaseId}/versions/${versionId}`).then(res => res.data),
    restore: (testCaseId, versionId) =>
        api.post(`/tests/${testCaseId}/versions/${versionId}/restore`).then(res => res.data),
};

// ── Requirements & traceability (007-req-traceability) ──
export const requirements = {
    list: () =>
        api.get('/requirements').then(res => res.data),
    create: (data) =>
        api.post('/requirements', data).then(res => res.data),
    get: (id, config) =>
        api.get(`/requirements/${id}`, config).then(res => res.data),
    update: (id, data) =>
        api.put(`/requirements/${id}`, data).then(res => res.data),
    delete: (id) =>
        api.delete(`/requirements/${id}`),
    bulkDelete: (ids) =>
        api.post('/requirements/bulk-delete', { ids }),
    listChildren: (id) =>
        api.get(`/requirements/${id}/children`).then(res => res.data),
    createLink: (reqId, testCaseId) =>
        api.post(`/requirements/${reqId}/links`, { test_case_id: testCaseId }).then(res => res.data),
    deleteLink: (reqId, testCaseId) =>
        api.delete(`/requirements/${reqId}/links/${testCaseId}`),
    listByTestCase: (testCaseId) =>
        api.get(`/tests/${testCaseId}/requirements`).then(res => res.data),
    // 011-jira-confluence-import
    importSingle: (sourceType, sourceKey, includeChildren = false) =>
        api.post('/requirements/import', { source_type: sourceType, source_key: sourceKey, include_children: includeChildren }).then(res => res.data),
    bulkImport: (sourceType, sourceKeys, includeChildren = false) =>
        api.post('/requirements/bulk-import', { source_type: sourceType, source_keys: sourceKeys, include_children: includeChildren }).then(res => res.data),
    resync: (id) =>
        api.post(`/requirements/${id}/resync`).then(res => res.data),
    resyncResolve: (id, resolution, remoteTitle, remoteDescription) =>
        api.post(`/requirements/${id}/resync/resolve`, { resolution, remote_title: remoteTitle, remote_description: remoteDescription }).then(res => res.data),
    unlink: (id) =>
        api.post(`/requirements/${id}/unlink`).then(res => res.data),
    postToJira: (id) =>
        api.post(`/requirements/${id}/post-to-jira`).then(res => res.data),
};

export const traceability = {
    getMatrix: (config) =>
        api.get('/traceability', config).then(res => res.data),
};

// ── Jira integration (007-req-traceability) ──
export const jira = {
    getConfig: () =>
        api.get('/settings/jira', { _silent: true }).then(res => res.data),
    upsertConfig: (data) =>
        api.put('/settings/jira', data).then(res => res.data),
    fetchTicket: (ticketId) =>
        api.get(`/jira/ticket/${ticketId}`).then(res => res.data),
    // 011: JQL search for bulk import
    search: (jql, startAt = 0, maxResults = 25) =>
        api.post('/jira/search', { jql, start_at: startAt, max_results: maxResults }).then(res => res.data),
};

// ── Confluence integration (011-jira-confluence-import) ──
export const confluence = {
    getConfig: () =>
        api.get('/settings/confluence', { _silent: true }).then(res => res.data),
    upsertConfig: (data) =>
        api.put('/settings/confluence', data).then(res => res.data),
    listSpaces: (cursor, limit) => {
        const params = {};
        if (cursor) params.cursor = cursor;
        if (limit) params.limit = limit;
        return api.get('/confluence/spaces', { params }).then(res => res.data);
    },
    listPages: (spaceId, title, label, cursor, limit) => {
        const params = { space_id: spaceId };
        if (title) params.title = title;
        if (label) params.label = label;
        if (cursor) params.cursor = cursor;
        if (limit) params.limit = limit;
        return api.get('/confluence/pages', { params }).then(res => res.data);
    },
    getPage: (pageId) =>
        api.get(`/confluence/pages/${pageId}`).then(res => res.data),
    listChildPages: (pageId, cursor, limit) => {
        const params = {};
        if (cursor) params.cursor = cursor;
        if (limit) params.limit = limit;
        return api.get(`/confluence/pages/${pageId}/children`, { params }).then(res => res.data);
    },
};

// ── Test case execution history ──
export const listTestExecutions = (testCaseId) =>
    api.get(`/tests/${testCaseId}/executions`).then(res => res.data);

// ── Native defects ──
export const defects = {
    // The Defects register calls list() with no params: it fetches the whole set once and
    // filters/sorts/counts client-side. `params` stays supported because DefectLinkPanel
    // still searches server-side with { q }.
    list: (params = {}) => api.get('/defects', { params }).then(res => res.data),
    create: (data) => api.post('/defects', data).then(res => res.data),
    update: (id, data) => api.patch(`/defects/${id}`, data).then(res => res.data),
    // `fields` carries any of { status, severity, assignee_id }; an omitted field is left
    // unchanged on every selected defect and assignee_id: '' unassigns. The explicit `ids`
    // argument always wins, so a full payload from buildBulkPayload can be passed as `fields`.
    // Cap is 500 ids per call (httpx.MaxBulkIDs); the server 400s beyond that.
    bulkUpdate: (ids, fields = {}) => api.post('/defects/bulk-update', { ...fields, ids }).then(res => res.data),
    remove: (id) => api.delete(`/defects/${id}`),
    affectedTests: (id) => api.get(`/defects/${id}/tests`).then(res => res.data),
};

export const resultDefects = {
    list: (runId, resultId) => api.get(`/runs/${runId}/results/${resultId}/defect-links`).then(res => res.data),
    link: (runId, resultId, defectId) => api.post(`/runs/${runId}/results/${resultId}/defect-links`, { defect_id: defectId }).then(res => res.data),
    create: (runId, resultId, data) => api.post(`/runs/${runId}/results/${resultId}/defects`, data).then(res => res.data),
    unlink: (runId, resultId, defectId) => api.delete(`/runs/${runId}/results/${resultId}/defect-links/${encodeURIComponent(defectId)}`),
};

export const listRunDefects = (runId) => api.get(`/runs/${runId}/defect-links`).then(res => res.data);

export const testCaseDefects = {
    list: (testId) => api.get(`/tests/${testId}/defect-links`).then(res => res.data),
    link: (testId, defectId) => api.post(`/tests/${testId}/defect-links`, { defect_id: defectId }).then(res => res.data),
    unlink: (testId, defectId) => api.delete(`/tests/${testId}/defect-links/${encodeURIComponent(defectId)}`),
};

// ── AI Test Case Generation (010-ai-test-generation) ──
export const aiGeneration = {
    // Provider config (admin)
    listProviders: () =>
        api.get('/settings/llm-providers').then(res => res.data),
    createProvider: (data) =>
        api.post('/settings/llm-providers', data).then(res => res.data),
    updateProvider: (id, data) =>
        api.put(`/settings/llm-providers/${id}`, data).then(res => res.data),
    deleteProvider: (id) =>
        api.delete(`/settings/llm-providers/${id}`),
    testConnection: (id) =>
        api.post(`/settings/llm-providers/${id}/test`).then(res => res.data),
    setDefault: (id) =>
        api.post(`/settings/llm-providers/${id}/set-default`).then(res => res.data),
    // Coverage config
    getCoverageConfig: () =>
        api.get('/settings/ai-gen-coverage').then(res => res.data),
    updateCoverageConfig: (data) =>
        api.put('/settings/ai-gen-coverage', data).then(res => res.data),
    // Global AI master switch
    getFeatureSettings: () =>
        api.get('/settings/ai-features').then(res => res.data),
    updateFeatureSettings: (enabled) =>
        api.put('/settings/ai-features', { enabled }).then(res => res.data),
    // Prompt template
    getTemplate: () =>
        api.get('/settings/ai-gen-template').then(res => res.data),
    updateTemplate: (content) =>
        api.put('/settings/ai-gen-template', { content }).then(res => res.data),
    resetTemplate: () =>
        api.post('/settings/ai-gen-template/reset').then(res => res.data),
    // Parent template (for requirements with children)
    updateParentTemplate: (content) =>
        api.put('/settings/ai-gen-parent-template', { content }).then(res => res.data),
    resetParentTemplate: () =>
        api.post('/settings/ai-gen-parent-template/reset').then(res => res.data),
    // Soft cost budgets (ai-generation stage 6)
    getBudgetSettings: () =>
        api.get('/settings/ai-budgets').then(res => res.data),
    updateBudgetSettings: (data) =>
        api.put('/settings/ai-budgets', data).then(res => res.data),
    // Prompt preview (live, read-only — no LLM call)
    promptPreview: (data) =>
        api.post('/ai-gen/prompt-preview', data, { _silent: true }).then(res => res.data),
    // Generation
    generateTests: (requirementId, data) =>
        api.post(`/requirements/${requirementId}/generate-tests`, data).then(res => res.data),
    acceptGeneratedTests: (requirementId, data) =>
        api.post(`/requirements/${requirementId}/accept-generated-tests`, data).then(res => res.data),
    // ── Generation lifecycle (ai-generation-improvements) ──
    createGeneration: (data, config) =>
        api.post('/ai-generations', data, config).then(res => res.data),
    getGeneration: (runId) =>
        api.get(`/ai-generations/${runId}`).then(res => res.data),
    listGenerations: (requirementId) =>
        api.get('/ai-generations', { params: { requirement_id: requirementId } }).then(res => res.data),
    updateGenerationDraft: (runId, draftId, changes) =>
        api.patch(`/ai-generations/${runId}/drafts/${draftId}`, changes).then(res => res.data),
    rejectGenerationDraft: (runId, draftId, data) =>
        api.post(`/ai-generations/${runId}/drafts/${draftId}/reject`, data).then(res => res.data),
    restoreGenerationDraft: (runId, draftId) =>
        api.post(`/ai-generations/${runId}/drafts/${draftId}/restore`).then(res => res.data),
    acceptGeneration: (runId, data) =>
        api.post(`/ai-generations/${runId}/accept`, data).then(res => res.data),
    // ── Targeted regeneration (ai-generation-stage-5) ──
    regenerateGenerationDraft: (runId, draftId, data) =>
        api.post(`/ai-generations/${runId}/drafts/${draftId}/regenerate`, data).then(res => res.data),
    chooseGenerationDraft: (runId, draftId) =>
        api.post(`/ai-generations/${runId}/drafts/${draftId}/choose`).then(res => res.data),
    cancelGeneration: (runId) =>
        api.post(`/ai-generations/${runId}/cancel`, null, { _silent: true }).then(res => res.data),
};

// ── AI Test Import (014-ai-test-import) ──
export const aiImport = {
    parse: (data) =>
        api.post('/import/parse', data).then(res => res.data),
    accept: (data) =>
        api.post('/import/accept', data).then(res => res.data),
};

// ── AI Failure Analysis ──
// A failed attempt is stored too: the server answers 502 with { error, analysis }. The error is
// toasted by the interceptor; the call resolves with the stored row so the card can show it.
// Soft AI budgets: an analysis start that would exceed one answers 409 { category: 'budget' }.
// These calls ask (window.confirm) instead of toasting and resend with acknowledge_budget=true.
const confirmBudget = (message) => window.confirm(message);
const budgetConfig = (ack) => ({ _budgetAware: true, ...(ack ? { params: { acknowledge_budget: true } } : {}) });

export const analyzeRunResult = (runResultId) =>
    withBudgetConfirm((ack) => api.post(`/run-results/${runResultId}/analyze`, undefined, budgetConfig(ack)), confirmBudget)
        .then(r => r.data).catch((err) => {
            const stored = err?.response?.data?.analysis;
            if (stored) return stored;
            throw err;
        });

// Explain asks the default LLM for the explanation a stored TypeSafe decision lacks; it
// resolves with the same analysis, its decision unchanged. opts.scope 'result' explains a grouped
// result on its own evidence (its copied explanation may not fit it). opts.overrideInjection sends
// a failure flagged for possible prompt injection after the user confirmed. When the server asks
// for that override itself (409; Explain this result checks the clone's own evidence first), the
// same question is asked here and the request resent once.
const confirmInjection = (message) => window.confirm(message);
export const explainAnalysis = (runResultId, analysisId, opts = {}) =>
    withInjectionConfirm((o) => withBudgetConfirm((ack) => {
        const cfg = budgetConfig(ack);
        return api.post(`/run-results/${runResultId}/analyses/${analysisId}/explain`, undefined,
            { ...cfg, _injectionAware: true, params: { ...(cfg.params || {}), ...explainParams(o) } });
    }, confirmBudget), opts, confirmInjection).then(r => r.data);

export const listRunResultAnalyses = (runResultId) =>
    api.get(`/run-results/${runResultId}/analyses`).then(r => r.data);

export const getCurrentRunAnalyses = (runId) =>
    api.get(`/runs/${runId}/analyses/current`).then(r => r.data);

// acknowledgeBudget: the user already agreed (the banner's "Run anyway" after a budget skip).
export const analyzeRunFailures = (runId, { acknowledgeBudget = false } = {}) =>
    withBudgetConfirm((ack) => api.post(`/runs/${runId}/analyze-failures`, undefined, budgetConfig(ack)), confirmBudget, acknowledgeBudget)
        .then(r => r.data);

export const getRunAnalysisJob = (runId) =>
    api.get(`/runs/${runId}/analysis-job`, { _silent: true }).then(r => r.data);

export const cancelRunAnalysisJob = (runId) =>
    api.post(`/runs/${runId}/analysis-job/cancel`).then(r => r.data);

export const retryFailedRunAnalysis = (runId) =>
    withBudgetConfirm((ack) => api.post(`/runs/${runId}/analysis-job/retry-failed`, undefined, budgetConfig(ack)), confirmBudget)
        .then(r => r.data);

// Why a result was grouped semantically with its representative, and whether it can be split.
export const getSemanticMerge = (runResultId, analysisId) =>
    api.get(`/run-results/${runResultId}/analyses/${analysisId}/semantic`).then(r => r.data);

// Split the results sharing this error out of their semantic group; resolves with the queued job.
export const splitSemanticMerge = (runResultId, analysisId) =>
    withBudgetConfirm((ack) => api.post(`/run-results/${runResultId}/analyses/${analysisId}/split`, undefined, budgetConfig(ack)), confirmBudget)
        .then(r => r.data);

export const getFailureAnalysisSettings = () =>
    api.get('/settings/ai-failure-analysis').then(r => r.data);

export const updateFailureAnalysisSettings = (body) =>
    api.put('/settings/ai-failure-analysis', body).then(r => r.data);

export const resetFailureAnalysisPrompt = () =>
    api.post('/settings/ai-failure-analysis/prompt/reset').then(r => r.data);

// The auto-apply accuracy gate at a minimum confidence (80–99). The response's min_confidence
// names the threshold its figures were measured at. Silent: the card shows its own error line.
export const getAutoApplyGate = (minConfidencePct) =>
    api.get('/settings/ai-failure-analysis/auto-apply-gate', { params: { min_confidence: minConfidencePct }, _silent: true })
        .then(r => r.data);

export const getFailureAnalysisAccuracy = (days = 30, policyVersion = '') =>
    api.get('/ai/failure-analysis/accuracy', {
        params: policyVersion ? { days, policy_version: policyVersion } : { days },
    }).then(r => r.data);

export const getTypeSafeSettings = () =>
    api.get('/settings/typesafe').then(r => r.data);

export const updateTypeSafeSettings = (patch) =>
    api.put('/settings/typesafe', patch).then(r => r.data);

export const testTypeSafeConnection = () =>
    api.post('/settings/typesafe/test').then(r => r.data);

// ── Database Backups (015-database-backups) ──
export const backups = {
    list: () => api.get('/backups').then(res => res.data),
    create: () => api.post('/backups').then(res => res.data),
    get: (id) => api.get(`/backups/${id}`).then(res => res.data),
    delete: (id) => api.delete(`/backups/${id}`).then(res => res.data),
    download: (id) => api.get(`/backups/${id}/download`, { responseType: 'blob' }),
    restore: (id, confirmation) =>
        api.post(`/backups/${id}/restore`, { confirmation }).then(res => res.data),
    uploadRestore: (file, confirmation) => {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('confirmation', confirmation);
        return api.post('/backups/upload-restore', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then(res => res.data);
    },
    schedule: {
        get: () => api.get('/backup-schedule').then(res => res.data),
        update: (data) => api.put('/backup-schedule', data).then(res => res.data),
    },
    maintenanceStatus: () => api.get('/maintenance-status', { _silent: true }).then(res => res.data),
};

export default api;

// Which uses of TypeSafe.ai outside failure analysis would run now (import_structure,
// draft_review, defect_assist, search_rerank → bool). Silent: a control simply stays hidden.
export const getTypeSafeUses = () =>
    api.get('/ai/typesafe/features', { _silent: true }).then(r => r.data);

// Defect assist: TypeSafe.ai suggests a severity and lists open defects the new one may duplicate.
export const assistDefect = (body) => api.post('/defects/assist', body).then(r => r.data);
